import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { Query } from "@tanstack/react-query";
import { EventBus, Events } from "jiffy-store";
import {
  CACHED_DELIVERY_DATE_QUERY_KEY,
  fetchCachedDeliveryDateQuery
} from "../api/useCachedDeliveryDate";

const isEstimatedDeliveryQuery = (query: Query): boolean => {
  const key = query.queryKey;
  return (
    Array.isArray(key) &&
    (key[0] === "deliveryEstimates" || (key[0] === "estimatedDelivery" && key.length >= 2))
  );
};

/**
 * Hook that listens for address updates and refreshes delivery date queries
 * This is used when the address is updated without a page refresh
 *
 * Always tries to fetch cached delivery date first.
 * If the response is undefined, falls back to refetching estimated delivery queries.
 */
export function useDeliveryDateRefresh() {
  const queryClient = useQueryClient();

  useEffect(() => {
    const handleAddressUpdate = async () => {
      // Always try to fetch cached delivery date first
      // Invalidate first to ensure we get fresh data for the new zipcode
      queryClient.invalidateQueries({
        queryKey: CACHED_DELIVERY_DATE_QUERY_KEY
      });

      try {
        // Fetch fresh cached delivery date for the new zipcode
        // This will make a new API call since we invalidated above
        const cachedDeliveryDate = await queryClient.fetchQuery({
          queryKey: CACHED_DELIVERY_DATE_QUERY_KEY,
          queryFn: fetchCachedDeliveryDateQuery
        });

        // Explicitly set query data to ensure all subscribers are notified of the update
        // This is necessary because enabled: false queries might not automatically
        // trigger re-renders when cache is updated via fetchQuery
        queryClient.setQueryData(CACHED_DELIVERY_DATE_QUERY_KEY, cachedDeliveryDate);

        // If cached delivery date is null or undefined (including when API returns error),
        // invalidate estimated delivery queries so they refetch with new address
        if (cachedDeliveryDate == null) {
          // Use refetchType: 'all' to refetch even inactive queries (enabled: false)
          queryClient.invalidateQueries({
            predicate: isEstimatedDeliveryQuery,
            refetchType: "all"
          });
        }
      } catch {
        // On error, invalidate estimated delivery queries so they refetch
        queryClient.invalidateQueries({
          predicate: isEstimatedDeliveryQuery,
          refetchType: "all"
        });
      }
    };

    EventBus.on(Events.LOCAL_DELIVERY_ADDRESS_UPDATED, handleAddressUpdate);

    return () => {
      // Pass undefined for scope to match how EventBus.on was called
      EventBus.removeEventListener(
        Events.LOCAL_DELIVERY_ADDRESS_UPDATED,
        handleAddressUpdate,
        undefined
      );
    };
  }, [queryClient]);
}
